/**
 * Wire types for reviewer decisions (`POST /attribution/reviews`,
 * `GET /attribution/products/{id}/reviews`) and the QC vocabulary they share.
 *
 * Decisions are append-only: a changed mind is a new event and the latest one
 * per item stands. An "item" is the tuple the service groups on —
 * subject · component · attributeName · modelValue · checkName — so every event
 * about the same thing must send those five exactly as before.
 */

/** What a finding or a review is ABOUT. */
export const REVIEW_SUBJECTS = [
  'PRODUCT_TYPE',
  'LISTING_SHAPE',
  'CORE_ATTRIBUTE',
  'COMPONENT_ATTRIBUTE',
] as const;
export type ReviewSubject = (typeof REVIEW_SUBJECTS)[number];

/** What a reviewer decided. `WITHDRAWN` makes the item unreviewed again. */
export const REVIEW_DECISIONS = [
  'CORRECT',
  'WRONG',
  'ADDED',
  'FINDING_CONFIRMED',
  'FINDING_DISMISSED',
  'WITHDRAWN',
] as const;
export type ReviewDecision = (typeof REVIEW_DECISIONS)[number];

/** The 11 QC rules. The API 422s a `checkName` outside this set, so never send `''`. */
export const QC_CHECKS = [
  'UNSUPPORTED_CLAIM',
  'INPUT_CONFLICT',
  'CONTRADICTION',
  'FEED_MISMATCH',
  'VOCABULARY_GAP',
  'POSSIBLY_MISSED',
  'DETECTION_DOUBT',
  'LISTING_SHAPE_MISMATCH',
  'MISSING_PIECE',
  'PACK_COLOURS',
  'NO_ANSWERS',
] as const;
export type QcCheck = (typeof QC_CHECKS)[number];

/** How much a QC finding matters; `SUGGESTION` is a value the model may have missed. */
export const QC_SEVERITIES = ['HIGH', 'MEDIUM', 'SUGGESTION', 'INFO'] as const;
export type QcSeverity = (typeof QC_SEVERITIES)[number];

/** One decision to append. Optional fields are omitted rather than sent empty where it matters. */
export interface ReviewEventCreate {
  productId: string;
  subject: ReviewSubject;
  verdict: ReviewDecision;
  component?: string;
  attributeName?: string;
  /** The value judged, as the reviewer saw it — part of the item's identity. */
  modelValue?: string;
  /** The right value for WRONG and ADDED; empty WRONG = "should not be there". */
  correctedValue?: string;
  /** Only for FINDING_CONFIRMED / FINDING_DISMISSED (and their WITHDRAWN). */
  checkName?: QcCheck;
  note?: string;
  runId?: string;
  promptVersion?: string;
  extractorVersion?: string;
}

/** `POST /attribution/reviews` body. At most `MAX_REVIEW_EVENTS` events. */
export interface ReviewBatchCreate {
  events: ReviewEventCreate[];
}

/** The service's cap on events per POST. */
export const MAX_REVIEW_EVENTS = 500;

/** What a POST appended. */
export interface ReviewBatchResult {
  appended: number;
  eventIds: string[];
}

/** Where one reviewed item stands now — the latest decision per item, from the product view. */
export interface LatestReviewRead {
  productId: string;
  subject: string;
  component: string;
  attributeName: string;
  modelValue: string;
  checkName: string;
  /** Read leniently — a `ReviewDecision` value. */
  verdict: string;
  correctedValue: string;
  note: string;
  reviewer: string;
  decisions: number;
  reviewedAt: string | null;
}

/** One decision in a product's history (`GET /attribution/products/{id}/reviews`). */
export interface ReviewEventRead {
  eventId: string;
  productId: string;
  subject: string;
  component: string;
  attributeName: string;
  modelValue: string;
  verdict: string;
  correctedValue: string;
  checkName: string;
  note: string;
  runId: string;
  promptVersion: string;
  extractorVersion: string;
  reviewer: string;
  reviewedAt: string | null;
}
